import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import EmptyState from "@/components/feature/EmptyState";
import EpisodeList from "./components/EpisodeList";
import DetailSidebar from "./components/DetailSidebar";
import { getMediaById, getRecommendations } from "@/lib/media";
import { useFavorites } from "@/hooks/useFavorites";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function DetailPage() {
  const { id } = useParams();
  const item = getMediaById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [showPlayTip, setShowPlayTip] = useState(false);

  useDocumentMeta(
    item ? `【${item.title}】免费播放片大片介绍及详情页` : "影视详情 - 免费播放片大片",
    item
      ? `${item.title}（${item.year}，${item.region}，${item.genres.join("、")}）：${item.synopsis.slice(0, 80)}…`
      : "免费播放片大片影视内容详情页",
    item ? `${item.title},${item.genres.join(",")},免费播放片大片,影视详情,${item.region}影视` : "免费播放片大片",
  );

  if (!item) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-16 md:px-8 md:py-24">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="该影视内容不存在或已下架"
          description="你访问的影视内容可能已被移除，或链接地址有误。返回首页发现更多精彩影视。"
          action={
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-950 whitespace-nowrap transition-colors hover:bg-accent-400"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          }
        />
      </div>
    );
  }

  const related = getRecommendations(item, 4);
  const favorite = isFavorite(item.id);
  const categoryLink = item.type === "tv" ? "/tv" : "/movie";
  const categoryLabel = item.type === "tv" ? "电视剧" : "电影";

  return (
    <div className="bg-background-950">
      <section className="relative">
        <div className="absolute inset-0 h-full w-full">
          <img
            src={item.backdrop}
            alt={`《${item.title}》主视觉背景`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/85 to-background-950/60"></div>
        </div>

        <div className="relative mx-auto w-full max-w-7xl px-4 pb-10 pt-8 md:px-8 md:pb-14 md:pt-12">
          <nav aria-label="面包屑" className="mb-6 flex flex-wrap items-center gap-1.5 text-sm text-foreground-400">
            <Link to="/" className="transition-colors hover:text-accent-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={categoryLink} className="transition-colors hover:text-accent-400">
              {categoryLabel}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-200">{item.title}</span>
          </nav>

          <div className="flex flex-col gap-6 md:flex-row md:items-end md:gap-8">
            <div className="h-64 w-44 shrink-0 overflow-hidden rounded-lg border border-background-700 bg-background-800 md:h-80 md:w-56">
              <img
                src={item.cover}
                alt={`《${item.title}》海报`}
                title={`${item.title} - 免费播放片大片`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="min-w-0 flex-1">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-500/20 px-3 py-1 text-xs font-medium text-accent-300">
                <i className="ri-price-tag-3-line"></i>
                {item.type === "tv" ? "电视剧" : "电影"}
              </span>
              <h1 className="mt-3 font-heading text-2xl font-bold leading-tight text-foreground-50 md:text-4xl">
                {item.title}
              </h1>
              <p className="mt-2 text-sm text-accent-300 md:text-base">{item.tagline}</p>

              <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-foreground-200">
                <span className="flex items-center gap-1 text-accent-400">
                  <i className="ri-star-fill"></i>
                  <span className="font-semibold">{item.rating.toFixed(1)}</span>
                </span>
                <span>{item.year}</span>
                <span className="text-foreground-600">·</span>
                <span>{item.region}</span>
                <span className="text-foreground-600">·</span>
                <span className="flex flex-wrap gap-1.5">
                  {item.genres.map((genre) => (
                    <span key={genre} className="rounded-md bg-secondary-100/10 px-2 py-0.5 text-xs text-secondary-200">
                      {genre}
                    </span>
                  ))}
                </span>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowPlayTip(true)}
                  className="flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-950 whitespace-nowrap transition-colors hover:bg-accent-400"
                >
                  <i className="ri-play-fill text-lg"></i>
                  立即观看
                </button>
                <button
                  type="button"
                  onClick={() => toggleFavorite(item.id)}
                  aria-pressed={favorite}
                  className={`flex items-center gap-2 rounded-md border px-5 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                    favorite
                      ? "border-accent-500 bg-accent-500/15 text-accent-300"
                      : "border-background-600 text-foreground-100 hover:border-accent-400 hover:text-accent-400"
                  }`}
                >
                  <i className={favorite ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                  {favorite ? "已收藏" : "收藏"}
                </button>
                <Link
                  to={categoryLink}
                  className="flex items-center gap-2 rounded-md border border-background-600 px-5 py-2.5 text-sm font-medium text-foreground-100 whitespace-nowrap transition-colors hover:border-accent-400 hover:text-accent-400"
                >
                  <i className="ri-folder-line"></i>
                  所属分类：{categoryLabel}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-8 px-4 py-10 md:px-8 md:py-14 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className="rounded-lg border border-background-800 bg-background-900/50 p-5 md:p-6" aria-label="剧情简介">
            <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-50 md:text-xl">
              <i className="ri-file-text-line text-accent-400"></i>
              剧情简介
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-300 md:text-base">{item.synopsis}</p>
            <p className="mt-4 rounded-md bg-background-950/60 p-3 text-xs leading-relaxed text-foreground-400">
              <i className="ri-information-line mr-1 text-secondary-400"></i>
              温馨提示：本站为演示站点，暂未接入真实在线播放功能，播放入口仅作展示。
            </p>
          </section>

          {item.type === "tv" && item.episodes && item.episodes.length > 0 && (
            <EpisodeList episodes={item.episodes} />
          )}
        </div>

        <DetailSidebar item={item} related={related} />
      </div>

      {showPlayTip && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-background-950/80 px-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="播放提示"
          onClick={() => setShowPlayTip(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg border border-background-700 bg-background-900 p-6 text-center"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-500/15 text-accent-400">
              <i className="ri-movie-2-line text-3xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-50">暂无在线播放</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-400">
              《{item.title}》的在线播放功能正在筹备中，敬请期待。你可以先浏览剧情简介与相关推荐。
            </p>
            <button
              type="button"
              onClick={() => setShowPlayTip(false)}
              className="mt-5 w-full rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-950 whitespace-nowrap transition-colors hover:bg-accent-400"
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </div>
  );
}